import ky from "ky";
import { supabase } from "./supabase";
import {
  Profile,
  ApiKey,
  Plan,
  Subscription,
  UsageStats,
  CreateApiKeyRequest,
  CheckoutSessionRequest,
  ApiResponse,
} from "../types";

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8787";

// Custom error class for API errors
export class ApiError extends Error {
  public code: number;
  public success: boolean;

  constructor(message: string, code: number = 0) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.success = false;
  }
}

class ApiClient {
  public api: typeof ky; // Make api public so playground hooks can access it

  constructor() {
    this.api = ky.create({
      prefixUrl: API_BASE_URL,
      hooks: {
        beforeRequest: [
          async (request) => {
            try {
              const {
                data: { session },
                error,
              } = await supabase.auth.getSession();
              if (error) {
                console.error("Supabase session error:", error);
                throw new Error("Authentication failed: Unable to get session");
              }

              if (!session?.access_token) {
                throw new Error("Authentication failed: No access token found");
              }

              request.headers.set(
                "Authorization",
                `Bearer ${session.access_token}`
              );
              request.headers.set("Content-Type", "application/json");

              // Debug logging (remove in production)
              console.log("API Request:", {
                url: request.url,
                method: request.method,
                hasToken: !!session.access_token,
                tokenPrefix: session.access_token.substring(0, 20) + "...",
              });
            } catch (error) {
              console.error("Authentication setup failed:", error);
              throw error;
            }
          },
        ],
        afterResponse: [
          async (request, options, response) => {
            // Debug logging for responses
            if (!response.ok) {
              console.error("API Error Response:", {
                url: request.url,
                status: response.status,
                statusText: response.statusText,
              });

              // Try to get error details
              try {
                const errorData = await response.clone().json();
                console.error("Error details:", errorData);
              } catch (e) {
                console.error("Could not parse error response");
              }
            }
          },
        ],
      },
      retry: {
        limit: 2,
        methods: ["get"],
        statusCodes: [408, 413, 429, 500, 502, 503, 504],
      },
    });
  }

  // Helper method to handle standardized API responses
  public async handleApiResponse<T>(response: Promise<any>): Promise<T> {
    try {
      const result: ApiResponse<T> = await response;

      if (!result.success) {
        throw new ApiError(
          result.error || `API request failed (Code: ${result.code})`,
          result.code
        );
      }

      return result.data;
    } catch (error) {
      // Handle cases where the response might not be in the standardized format yet
      if (error instanceof ApiError) {
        throw error;
      }

      // If it's not an ApiError, it might be a legacy response format
      // Try to handle it gracefully
      const result = await response;
      if (result && typeof result === "object") {
        // Check if it's already the data we need (legacy format)
        if (
          result.plans ||
          result.apiKeys ||
          result.subscription ||
          result.profile ||
          result.success !== undefined
        ) {
          return result as T;
        }
      }

      throw new ApiError(
        error instanceof Error ? error.message : "Unknown API error"
      );
    }
  }

  // Helper method to check authentication status
  async checkAuth(): Promise<boolean> {
    try {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();
      if (error) {
        console.error("Auth check error:", error);
        return false;
      }
      return !!session?.access_token;
    } catch (error) {
      console.error("Auth check failed:", error);
      return false;
    }
  }

  // Auth methods
  async getProfile(): Promise<{ profile: Profile }> {
    const response = this.api.get("api/auth/profile").json();
    return this.handleApiResponse<{ profile: Profile }>(response);
  }

  async updateProfile(data: Partial<Profile>): Promise<{ profile: Profile }> {
    const response = this.api
      .post("api/auth/callback", { json: { session: { user: data } } })
      .json();
    return this.handleApiResponse<{ profile: Profile }>(response);
  }

  // API Keys methods
  async getApiKeys(): Promise<{ apiKeys: ApiKey[] }> {
    const response = this.api.get("api/keys").json();
    return this.handleApiResponse<{ apiKeys: ApiKey[] }>(response);
  }

  async getApiKey(keyId: string): Promise<{ apiKey: ApiKey }> {
    const response = this.api.get(`api/keys/${keyId}`).json();
    return this.handleApiResponse<{ apiKey: ApiKey }>(response);
  }

  async createApiKey(data: CreateApiKeyRequest): Promise<{ apiKey: ApiKey }> {
    const response = this.api.post("api/keys", { json: data }).json();
    return this.handleApiResponse<{ apiKey: ApiKey }>(response);
  }

  async deleteApiKey(keyId: string): Promise<{ success: boolean }> {
    const response = this.api.delete(`api/keys/${keyId}`).json();
    return this.handleApiResponse<{ success: boolean }>(response);
  }

  // Usage methods
  async getUsageStats(): Promise<UsageStats> {
    const response = this.api.get("api/usage").json();
    const result = await this.handleApiResponse<{ analytics: any }>(response);

    // Transform the analytics data to match the expected UsageStats format
    const analytics = result.analytics || [];

    // Calculate aggregated stats from analytics data
    let totalRequests = 0;
    let successfulRequests = 0;
    let failedRequests = 0;
    const topApiKeys: Array<{ keyId: string; name: string; requests: number }> =
      [];
    const dailyUsage: Array<{
      date: string;
      requests: number;
      success: number;
      failed: number;
    }> = [];

    analytics.forEach((keyAnalytics: any) => {
      if (keyAnalytics.data && Array.isArray(keyAnalytics.data)) {
        const keyRequests = keyAnalytics.data.reduce(
          (sum: number, day: any) =>
            sum + (day.success || 0) + (day.failed || 0),
          0
        );
        totalRequests += keyRequests;
        successfulRequests += keyAnalytics.data.reduce(
          (sum: number, day: any) => sum + (day.success || 0),
          0
        );
        failedRequests += keyAnalytics.data.reduce(
          (sum: number, day: any) => sum + (day.failed || 0),
          0
        );

        if (keyRequests > 0) {
          topApiKeys.push({
            keyId: keyAnalytics.unkeyId,
            name: keyAnalytics.keyName,
            requests: keyRequests,
          });
        }
      }
    });

    return {
      totalRequests,
      successfulRequests,
      failedRequests,
      avgResponseTime: 0, // Not available from current analytics
      avgSuccessRate:
        totalRequests > 0 ? (successfulRequests / totalRequests) * 100 : 0,
      requestsToday: 0, // Would need today's specific data
      requestsThisMonth: totalRequests, // Approximation based on available data
      topApiKeys: topApiKeys
        .sort((a, b) => b.requests - a.requests)
        .slice(0, 5),
      dailyUsage,
    };
  }

  async logUsage(data: {
    keyId: string;
    endpoint: string;
    success: boolean;
    responseTime: number;
  }): Promise<{ success: boolean }> {
    const response = this.api.post("api/usage/log", { json: data }).json();
    return this.handleApiResponse<{ success: boolean }>(response);
  }

  // Billing methods - Updated to handle new response structure
  async getPlans(): Promise<{ plans: Plan[] }> {
    // Add extra debugging for this specific endpoint
    const isAuthenticated = await this.checkAuth();
    if (!isAuthenticated) {
      throw new Error("User is not authenticated. Please log in.");
    }

    const response = this.api.get("api/billing/plans").json();
    return this.handleApiResponse<{ plans: Plan[] }>(response);
  }

  async getPlan(priceId: string): Promise<{ plan: Plan }> {
    const response = this.api.get(`api/billing/plans/${priceId}`).json();
    return this.handleApiResponse<{ plan: Plan }>(response);
  }

  async createCheckoutSession(
    data: CheckoutSessionRequest
  ): Promise<{ url: string }> {
    const response = this.api
      .post("api/billing/checkout", { json: data })
      .json();
    return this.handleApiResponse<{ url: string }>(response);
  }

  async createBillingPortalSession(): Promise<{ url: string }> {
    const response = this.api
      .post("api/billing/portal", {
        json: { returnUrl: window.location.origin + "/dashboard/billing" },
      })
      .json();
    return this.handleApiResponse<{ url: string }>(response);
  }

  async getSubscription(): Promise<{
    subscription: Subscription | null;
    subscriptions: Subscription[] | null;
  }> {
    const response = this.api.get("api/billing/subscription").json();
    return this.handleApiResponse<{
      subscription: Subscription | null;
      subscriptions: Subscription[] | null;
    }>(response);
  }

  // Convenience methods for common operations
  async createCheckoutSessionWithDefaults(
    priceId: string
  ): Promise<{ url: string }> {
    const response = this.api
      .post("api/billing/checkout", {
        json: {
          priceId: priceId,
        },
      })
      .json();
    return this.handleApiResponse<{ url: string }>(response);
  }

  // Helper method to get current user's subscription with plan details
  async getCurrentSubscriptionWithPlan(): Promise<{
    subscription: Subscription | null;
  }> {
    const result = await this.getSubscription();
    if (result.subscription?.plan_id) {
      // If we have a plan_id, we could fetch plan details from Supabase
      // For now, we rely on the server to include plan details
    }
    return result;
  }

  // Add these methods to the ApiClient class

  async getCredits(): Promise<{
    balance: number;
    unlimited: boolean;
    payAsYouGoEnabled: boolean;
    keyId: string;
  }> {
    const response = this.api.get("api/billing/credits").json();
    return this.handleApiResponse<{
      balance: number;
      unlimited: boolean;
      payAsYouGoEnabled: boolean;
      keyId: string;
    }>(response);
  }

  async topupCredits(data: {
    credits: number;
    paymentMethodId?: string;
  }): Promise<any> {
    const response = this.api
      .post("api/billing/credits/topup", {
        json: data,
      })
      .json();
    return this.handleApiResponse<any>(response);
  }

  async triggerAutoTopup(data: {
    threshold?: number;
    topupAmount?: number;
  }): Promise<any> {
    const response = this.api
      .post("api/billing/credits/auto-topup", {
        json: data,
      })
      .json();
    return this.handleApiResponse<any>(response);
  }

  async updateBillingPreferences(preferences: {
    payAsYouGo: boolean;
  }): Promise<{ preferences: { payAsYouGo: boolean } }> {
    const response = this.api
      .put("api/billing/preferences", {
        json: preferences,
      })
      .json();
    return this.handleApiResponse<{ preferences: { payAsYouGo: boolean } }>(
      response
    );
  }

  async getBillingPreferences(): Promise<{
    preferences: { payAsYouGo: boolean };
  }> {
    const response = this.api.get("api/billing/preferences").json();
    return this.handleApiResponse<{ preferences: { payAsYouGo: boolean } }>(
      response
    );
  }

  async getApiKeyAnalytics(keyId: string): Promise<{ analytics: any }> {
    const response = this.api.get(`api/keys/${keyId}/analytics`).json();
    return this.handleApiResponse<{ analytics: any }>(response);
  }

  // Add this method to your ApiClient class
  async subscribeFreePlan(): Promise<{
    message: string;
    plan?: any;
    subscription?: any;
    alreadySubscribed?: boolean;
  }> {
    const response = this.api.post("api/billing/subscribe-free-plan").json();
    return this.handleApiResponse<{
      message: string;
      plan?: any;
      subscription?: any;
      alreadySubscribed?: boolean;
    }>(response);
  }

  // Playground methods (Note: tools/resources return mock data for connection testing)
  async getPlaygroundTools(serverName: string): Promise<any[]> {
    const response = this.api.get(`api/playground/servers/${encodeURIComponent(serverName)}/tools`).json();
    return this.handleApiResponse<any[]>(response);
  }

  async callPlaygroundTool(serverName: string, toolName: string, args: any = {}): Promise<any> {
    const response = this.api.post(`api/playground/servers/${encodeURIComponent(serverName)}/tools/${encodeURIComponent(toolName)}/call`, {
      json: { arguments: args }
    }).json();
    return this.handleApiResponse<any>(response);
  }

  async getPlaygroundResources(serverName: string): Promise<any[]> {
    const response = this.api.get(`api/playground/servers/${encodeURIComponent(serverName)}/resources`).json();
    return this.handleApiResponse<any[]>(response);
  }

  async getPlaygroundResource(serverName: string, uri: string): Promise<any> {
    const response = this.api.post(`api/playground/servers/${encodeURIComponent(serverName)}/resources/get`, {
      json: { uri }
    }).json();
    return this.handleApiResponse<any>(response);
  }

  async getPlaygroundHealth(): Promise<any> {
    const response = this.api.get("api/playground/health").json();
    return this.handleApiResponse<any>(response);
  }
}

export const apiClient = new ApiClient();
